/**
 * Relative-time formatting (P2-8, UX-REVIEW §4 item 8).
 *
 * Turns an ISO timestamp into friendly prose ("2 minutes ago", "yesterday") using the
 * built-in `Intl.RelativeTimeFormat` — no new dependency. Old or invalid timestamps fall
 * back to a plain locale date so a deliverable from last quarter doesn't read as
 * "13 weeks ago".
 *
 * Locale comes from the active i18n language (en/es). Lists should keep the absolute
 * timestamp available on hover via `formatAbsolute` in a `title` attribute.
 */
import i18n from "../i18n";

/** How old (in days) a timestamp may be before we switch to an absolute date. */
const ABSOLUTE_AFTER_DAYS = 7;

function activeLocale(): string {
  // i18n.language can be e.g. "en-US"; Intl handles the full BCP-47 tag fine, and falls
  // back gracefully if it's just "en"/"es".
  return i18n.language || "en";
}

/** Absolute, locale-aware date+time (used for the hover title). */
export function formatAbsolute(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  try {
    return new Intl.DateTimeFormat(activeLocale(), {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(d);
  } catch {
    return d.toLocaleString();
  }
}

const DIVISIONS: { amount: number; unit: Intl.RelativeTimeFormatUnit }[] = [
  { amount: 60, unit: "second" },
  { amount: 60, unit: "minute" },
  { amount: 24, unit: "hour" },
  { amount: 7, unit: "day" },
  { amount: 4.34524, unit: "week" },
  { amount: 12, unit: "month" },
  { amount: Number.POSITIVE_INFINITY, unit: "year" },
];

/**
 * "2 minutes ago", "in 3 hours", "yesterday". For timestamps older than a week, or any
 * value we can't parse, returns an absolute locale date instead.
 */
export function formatRelative(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;

  const diffMs = d.getTime() - Date.now();
  const diffDays = Math.abs(diffMs) / 86_400_000;

  // Far in the past → a real date reads better than "13 weeks ago".
  if (diffDays > ABSOLUTE_AFTER_DAYS) return formatAbsolute(iso);

  const locale = activeLocale();
  let rtf: Intl.RelativeTimeFormat;
  try {
    rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  } catch {
    return formatAbsolute(iso);
  }

  let duration = diffMs / 1000; // seconds, signed (negative = past)
  for (const division of DIVISIONS) {
    if (Math.abs(duration) < division.amount) {
      return rtf.format(Math.round(duration), division.unit);
    }
    duration /= division.amount;
  }
  return formatAbsolute(iso);
}
